import { useEffect, useRef, useCallback } from 'react'
import { fetchEventSource } from '@microsoft/fetch-event-source'
import { useAuthStore } from '@/stores/useAuthStore'

export interface SSEMessage {
  [key: string]: unknown
}

/** Typed payload for progress-tracking SSE streams (processing, export, upload). */
export interface ProgressSSEMessage extends SSEMessage {
  percent?: number
  stage?: string
  status?: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED'
  progress?: number
  message?: string
  error?: string
}

export interface SSEEvent {
  type: string // 'progress', 'complete', etc.
  data: SSEMessage
}

export interface UseSSEOptions {
  url: string | null
  onMessage?: (event: SSEEvent) => void
  onError?: (error: Error) => void
  onComplete?: (event: SSEEvent) => void
  onReconnect?: () => void
  enabled?: boolean
  /** When true, onerror returns a retry interval instead of throwing (default: false) */
  retry?: boolean
  /** Milliseconds between retries when retry is enabled (default: 3000) */
  retryInterval?: number
  /** When true, auto-disconnect on COMPLETED/FAILED status (default: true) */
  autoDisconnectOnTerminal?: boolean
  /** When true, keep the SSE connection open even when the tab is hidden (default: false) */
  openWhenHidden?: boolean
}

/**
 * Generic SSE hook for subscribing to server-sent events
 * Uses @microsoft/fetch-event-source to support Bearer token authentication
 * Automatically disconnects on COMPLETED or FAILED status
 * Cleans up on unmount
 *
 * @param options.url - SSE endpoint URL (can be relative or absolute)
 * @param options.onMessage - Called for each message received
 * @param options.onError - Called on connection or parsing errors
 * @param options.onComplete - Called when status is COMPLETED or FAILED
 * @param options.enabled - Whether to connect (default: true when url is provided)
 */
export function useSSE({
  url,
  onMessage,
  onError,
  onComplete,
  onReconnect,
  enabled = true,
  retry = false,
  retryInterval = 3000,
  autoDisconnectOnTerminal = true,
  openWhenHidden = false,
}: UseSSEOptions) {
  const abortControllerRef = useRef<AbortController | null>(null)
  const accessToken = useAuthStore(state => state.accessToken)

  // Store callbacks in refs to avoid re-running the effect when they change
  const onMessageRef = useRef(onMessage)
  const onErrorRef = useRef(onError)
  const onCompleteRef = useRef(onComplete)
  const onReconnectRef = useRef(onReconnect)

  // Update refs on each render so we always call the latest callback
  onMessageRef.current = onMessage
  onErrorRef.current = onError
  onCompleteRef.current = onComplete
  onReconnectRef.current = onReconnect

  const disconnect = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
  }, [])

  useEffect(() => {
    // Don't connect if disabled or no URL
    if (!enabled || !url) {
      return
    }

    // Build the full URL
    let fullUrl: string
    try {
      // Check if it's already an absolute URL
      if (url.startsWith('http://') || url.startsWith('https://')) {
        fullUrl = url
      } else {
        // Relative URL - prepend API base URL
        const baseUrl = import.meta.env.VITE_API_BASE_URL || ''
        fullUrl = `${baseUrl}${url}`
      }
    } catch (e) {
      onErrorRef.current?.(
        new Error(`Invalid SSE URL: ${url}`.concat(e as string))
      )
      return
    }

    // Create AbortController for cleanup
    const abortController = new AbortController()
    abortControllerRef.current = abortController

    // Start SSE connection with Bearer token authentication
    fetchEventSource(fullUrl, {
      method: 'GET',
      headers: {
        ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
      },
      signal: abortController.signal,
      onopen: async response => {
        if (!response.ok) {
          throw new Error(`SSE connection failed: ${response.status}`)
        }
      },
      onmessage: event => {
        try {
          const rawData = event.data?.trim() ?? ''

          // Ignore heartbeat/keepalive comment frames that carry no event name/data.
          if (!event.event && rawData.length === 0) {
            return
          }

          const data: SSEMessage = rawData.length > 0 ? JSON.parse(rawData) : {}
          const sseEvent: SSEEvent = { type: event.event || 'message', data }

          onMessageRef.current?.(sseEvent)

          // Terminal state detection: 'complete' event OR COMPLETED/FAILED status
          if (
            autoDisconnectOnTerminal &&
            (event.event === 'complete' ||
              data.status === 'COMPLETED' ||
              data.status === 'FAILED')
          ) {
            onCompleteRef.current?.(sseEvent)
            disconnect()
          }
        } catch (e) {
          onErrorRef.current?.(new Error(`Failed to parse SSE message: ${e}`))
        }
      },
      onerror: error => {
        onErrorRef.current?.(
          error instanceof Error ? error : new Error('SSE connection error')
        )
        if (retry) {
          onReconnectRef.current?.()
          return retryInterval
        }
        // Throw to stop retrying
        throw error
      },
      openWhenHidden,
      onclose: () => {
        if (retry) {
          onReconnectRef.current?.()
          throw new Error('SSE stream closed, reconnecting')
        }
      },
    }).catch(error => {
      // Handle errors that occur during connection setup
      // AbortError is expected when we disconnect intentionally
      if (error.name !== 'AbortError') {
        onErrorRef.current?.(
          error instanceof Error ? error : new Error('SSE connection error')
        )
      }
    })

    // Cleanup on unmount or when dependencies change
    return () => {
      disconnect()
    }
  }, [
    url,
    enabled,
    accessToken,
    disconnect,
    retry,
    retryInterval,
    autoDisconnectOnTerminal,
    openWhenHidden,
  ])

  return { disconnect }
}
